import type { MouseEventHandler, PropsWithChildren } from "react";
import { useModuleBEM, useUiStyles } from "@b2bportal/core-themes";
import { IconNameEnum } from "@b2bportal/core-themes";
import { CoreUiComponents } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  Icon,
} from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FilterDialogContainer.module.scss";

export const FilterDialogSection = ({
  title,
  content,
}: {
  title: string;
  content: JSX.Element;
}) => {
  const styles = useUiStyles(
    CoreUiComponents.FilterDialogContainer,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.FilterDialogContainer
  );
  return (
    <div className={cn("section")}>
      <div className={cn("section-title")}>{title}</div>
      <div className={cn("section-content")}>{content}</div>
    </div>
  );
};

export interface FilterDialogComponentProps {
  className?: string;
  onClickDialogTrigger?: MouseEventHandler<HTMLButtonElement>;
  onClickReset: MouseEventHandler<HTMLButtonElement>;
  onClickApply: MouseEventHandler<HTMLButtonElement>;
}
export const FilterDialogContainer = ({
  children,
  className,
  onClickDialogTrigger,
  onClickReset,
  onClickApply,
}: PropsWithChildren<FilterDialogComponentProps>) => {
  const styles = useUiStyles(
    CoreUiComponents.FilterDialogContainer,
    defaultStyles
  );
  const [block, cn] = useModuleBEM(
    styles,
    CoreUiComponents.FilterDialogContainer
  );

  const { t } = useI18nContext();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <button
          className={clsx(cn("button"), className)}
          onClick={(e) => onClickDialogTrigger?.(e)}
        >
          <span className={cn("button-text")}>
            {t("searchFilter.allFilters")}
          </span>
          <Icon iconName={IconNameEnum.filter} />
        </button>
      </DialogTrigger>
      <DialogContent
        title={t("core-ui.filters.dialog.openerLabel")}
        className={clsx(block, className)}
        bottomFixedContent={
          <div className={cn("bottom-section")}>
            <DialogClose asChild>
              <button onClick={onClickReset} className={cn("reset-button")}>
                {t("core-ui.filters.dialog.clear")}
              </button>
            </DialogClose>
            <DialogClose asChild>
              <button className={cn("apply-button")} onClick={onClickApply}>
                {t("core-ui.filters.dialog.apply")}
              </button>
            </DialogClose>
          </div>
        }
      >
        {children}
      </DialogContent>
    </Dialog>
  );
};
